import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedSite } from "building-types/site/site";
import { BuiltInSiteType } from "building-types/site/site";
import type { SiteShape } from "building-types/site/siteShape";
import type { RWTable, Table } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { planarGraphTracePerimeter } from "geom/planarGraph";
import { makeContourSite } from "../elements/site/siteContour";
import { makeDynamicSite } from "../elements/site/siteDynamic";
import { makeWhiteBoxSite } from "../elements/site/siteWhiteBox";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { sites, optionSets, instanceOptionSets, siteShapes },
  optionChoices: allOptionChoices,
  settings,
  planarGraphs,
  doorPlacements,
  buildingBounds,
  levels,
  entityDisabledByGroup,
  rooms,
  roomsByLevel,
  mirror,
  mirrorTranslationX,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const filteredOptionChoices = resolveOptionsInFormula(sites, definitions, allOptionChoices);
  return {
    optionChoices: filteredOptionChoices,
    allOptionChoices,
    definitions,
    siteShapes,
    levels,
    doorPlacements,
    buildingBounds,
    planarGraphs,
    garageSettings: settings.garage,
    entityDisabledByGroup,
    planarGraphTracePerimeter,
    docSites: sites,
    rooms,
    roomsByLevel,
    mirror,
    mirrorTranslationX,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  optionChoices: "shallow",
});

function generate({
  levels,
  doorPlacements,
  buildingBounds,
  planarGraphs,
  garageSettings,
  optionChoices,
  allOptionChoices,
  definitions,
  entityDisabledByGroup,
  docSites,
  siteShapes,
  rooms,
  roomsByLevel,
  mirror,
  mirrorTranslationX,
}: ReturnType<typeof select>): {
  sites: Table<GeneratedSite>;
  siteShapes: Table<SiteShape>;
} {
  const sites: RWTable<GeneratedSite> = {};
  const bldgSiteShapes: RWTable<SiteShape> = {};

  docSites = evalFormulas(docSites, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const siteId in docSites) {
    const site = docSites[siteId];
    const activeSiteShapes: SiteShape[] = [];

    if (site.disabled) {
      continue;
    }

    let generatedSite: GeneratedSite | undefined;

    switch (site.type) {
      case BuiltInSiteType.DYNAMIC:
        generatedSite = makeDynamicSite({
          site,
          doorPlacements,
          buildingBounds,
          planarGraphs,
          garageSettings,
          levels,
        });
        break;
      case BuiltInSiteType.STATIC_CONTOUR:
        // Collect Site Shapes
        for (const shapeId in siteShapes) {
          const shapeRaw = siteShapes[shapeId];
          if (!shapeRaw) {
            continue;
          }
          if (shapeRaw.parentId !== site.id) {
            continue;
          }

          const shapeOptionChoices = resolveOptionsInFormula(shapeRaw, definitions, allOptionChoices);

          const shapeEvaluated = evalFormulas(
            shapeRaw,
            (v) => shapeOptionChoices[v],
            undefined,
            entityDisabledByGroup,
          ) as SiteShape;

          if (shapeEvaluated.disabled) {
            continue; // Skip disabled shapes
          }

          activeSiteShapes.push(shapeEvaluated);
        }

        generatedSite = makeContourSite({
          site,
          siteShapes: activeSiteShapes,
          buildingBounds,
          levels,
          rooms,
          roomsByLevel,
          mirror,
          mirrorTranslationX,
        });
        break;
      case BuiltInSiteType.WHITE_BOX:
        generatedSite = makeWhiteBoxSite({
          site,
          doorPlacements,
          buildingBounds,
          planarGraphs,
          garageSettings,
          levels,
        });
        break;
      default: {
      }
    }

    if (activeSiteShapes.length > 0) {
      for (const shape of activeSiteShapes) {
        bldgSiteShapes[shape.id] = shape;
      }
    }

    if (generatedSite) {
      sites[generatedSite.id] = generatedSite;
    }
  }

  return { sites, siteShapes: bldgSiteShapes };
}

export default memo(select, generate, compare);
